import { useEffect, useState } from 'react';

/**
 * The responsive viewport snapshot the HUD reads where CSS/Tailwind can't express the need (clamping the
 * persisted, draggable chat-window size to the current screen — see {@link import('../features/office/HUD')}).
 *
 * Breakpoints mirror Tailwind's defaults so JS and class-based logic never drift:
 *   • isTablet  — width < lg (1024): tablet landscape & below (touch territory, tighter chrome).
 *   • isCompact — width < md (768): phones / narrow portrait (degrade gracefully, never the design target).
 *   • isLandscape — width ≥ height (orientation cue).
 */
export interface Viewport {
  width: number;
  height: number;
  /** Width < 1024 (Tailwind `lg`). Tablet landscape and below. */
  isTablet: boolean;
  /** Width < 768 (Tailwind `md`). Below the supported range — degrade, don't crash. */
  isCompact: boolean;
  isLandscape: boolean;
}

/** Tailwind's default `lg` breakpoint — the desktop / tablet divide. */
const LG = 1024;
/** Tailwind's default `md` breakpoint — the tablet / phone divide. */
const MD = 768;

function read(): Viewport {
  // SSR / non-DOM guard: assume a roomy desktop so the first paint is the desktop layout.
  if (typeof window === 'undefined') {
    return { width: 1280, height: 800, isTablet: false, isCompact: false, isLandscape: true };
  }
  const width = window.innerWidth;
  const height = window.innerHeight;
  return {
    width,
    height,
    isTablet: width < LG,
    isCompact: width < MD,
    isLandscape: width >= height,
  };
}

/**
 * A tiny, dependency-free viewport hook. Listens to a SINGLE `resize` event, coalesced through one
 * `requestAnimationFrame` so a drag-resize storm collapses to ≤1 state update per frame (Performance
 * Engineer: no polling loop, negligible cost). State only updates when a value actually changes, so a
 * resize that doesn't cross a breakpoint or change a dimension is a no-op for consumers.
 */
export function useViewport(): Viewport {
  const [vp, setVp] = useState<Viewport>(read);

  useEffect(() => {
    let frame = 0;
    const onResize = (): void => {
      if (frame) return; // already scheduled for this frame
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const next = read();
        setVp((prev) =>
          prev.width === next.width &&
          prev.height === next.height &&
          prev.isTablet === next.isTablet &&
          prev.isCompact === next.isCompact &&
          prev.isLandscape === next.isLandscape
            ? prev
            : next,
        );
      });
    };
    window.addEventListener('resize', onResize);
    // Re-read once on mount in case the viewport changed between the initial state and the effect.
    onResize();
    return () => {
      window.removeEventListener('resize', onResize);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  return vp;
}
